class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, { once: true });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const sections = parsedState?.sections || {};
    const drawerHTML = sections['cart-drawer'];
    if (typeof drawerHTML !== 'string' || !drawerHTML.trim()) {
      console.error('MAli cart drawer: missing cart-drawer section in AJAX response.', parsedState);
      return;
    }

    const parsedDrawer = this.getSectionDOM(drawerHTML, '#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');
    if (!parsedDrawer || !currentDrawer) {
      console.error('MAli cart drawer: #CartDrawer missing from returned or current markup.');
      return;
    }

    // The previous code removed is-empty only from .drawer__inner.
    // CSS also checks cart-drawer.is-empty, which hid the header and cart items
    // after adding the first product until the next full page refresh.
    const returnedCartDrawer = this.getSectionDOM(drawerHTML, 'cart-drawer');
    const emptyFromResponse = returnedCartDrawer
      ? returnedCartDrawer.classList.contains('is-empty')
      : !!parsedDrawer.querySelector('.drawer__inner-empty');

    this.classList.toggle('is-empty', emptyFromResponse);
    currentDrawer.innerHTML = parsedDrawer.innerHTML;
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', emptyFromResponse);

    const bubbleHTML = sections['cart-icon-bubble'];
    const currentBubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
    if (typeof bubbleHTML === 'string' && currentBubbleSection) {
      const incomingBubble = this.getSectionDOM(bubbleHTML, '.shopify-section');
      if (incomingBubble) currentBubbleSection.innerHTML = incomingBubble.innerHTML;
    }

    this.productId = parsedState.id;
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
